import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faXmark,
  faInfoCircle,
  faChartLine,
  faFileLines,
  faTerminal,
  faPlay,
  faStop,
  faRotate,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { useContainerActions } from '../hooks/useContainerActions';
import ContainerStats from './ContainerStats';
import ContainerLogs from './ContainerLogs';
import ContainerExec from './ContainerExec';
import { dockerService } from '../services/dockerService';

/**
 * ContainerDetails Component
 * Full-screen view with container details in tabs
 */
export default function ContainerDetails({ container, onClose, onUpdate }) {
  const [activeTab, setActiveTab] = useState('info');
  const [details, setDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [showTerminal, setShowTerminal] = useState(false);
  const { start, stop, restart, loading } = useContainerActions(container.id, (action) => {
    if (onUpdate) {
      onUpdate();
    }
  });

  // Fetch detailed info
  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoadingDetails(true);
        const data = await dockerService.getContainerDetails(container.id);
        setDetails(data);
      } catch (error) {
        console.error('Error fetching details:', error);
      } finally {
        setLoadingDetails(false);
      }
    };

    fetchDetails();
  }, [container.id]);

  // Telegram BackButton
  useEffect(() => {
    if (window.Telegram?.WebApp?.BackButton) {
      window.Telegram.WebApp.BackButton.onClick(onClose);
      window.Telegram.WebApp.BackButton.show();
      return () => {
        window.Telegram.WebApp.BackButton.offClick(onClose);
        window.Telegram.WebApp.BackButton.hide();
      };
    }
  }, [onClose]);

  const tabs = [
    { id: 'info', label: 'Info', icon: faInfoCircle },
    { id: 'stats', label: 'Stats', icon: faChartLine },
    { id: 'logs', label: 'Logs', icon: faFileLines }
  ];

  const getContainerName = () => {
    if (Array.isArray(container.names) && container.names.length > 0) {
      return container.names[0].replace(/^\//, '');
    }
    return 'unnamed';
  };

  const handleAction = async (action) => {
    try {
      await action();
      if (onUpdate) {
        setTimeout(onUpdate, 1000);
      }
    } catch (error) {
      alert(`Ошибка: ${error.message}`);
    }
  };

  const canStart = ['exited', 'created'].includes(container.state);
  const canStop = ['running', 'paused'].includes(container.state);

  const stateColors = {
    running: 'text-green-500',
    paused: 'text-yellow-500',
    exited: 'text-red-500',
    created: 'text-gray-500'
  };

  // Show terminal as separate full-screen component
  if (showTerminal) {
    return <ContainerExec container={container} onClose={() => setShowTerminal(false)} />;
  }

  return (
    <div className="h-full bg-theme-bg-color z-50 flex flex-col">
      {/* Header */}
      <div className="bg-theme-secondary-bg-color border-b border-theme-section-separator-color">
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-theme-text-color truncate">{getContainerName()}</h2>
            <p className={`text-sm ${stateColors[container.state]} capitalize`}>{container.state}</p>
          </div>
          <button
            onClick={onClose}
            className="ml-4 w-10 h-10 rounded-full bg-theme-bg-color flex items-center justify-center hover:bg-theme-section-separator-color transition-colors"
          >
            <FontAwesomeIcon icon={faXmark} className="text-theme-text-color" />
          </button>
        </div>

        {/* Action Buttons */}
        <div className="px-3 sm:px-5 pb-4 flex flex-wrap gap-2">
          {canStart && (
            <button
              onClick={() => handleAction(start)}
              disabled={loading}
              className="flex-1 min-w-[80px] py-2.5 rounded-lg bg-green-500 text-white hover:bg-green-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-1 sm:gap-2 text-sm sm:text-base"
            >
              {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPlay} />}
              <span>Start</span>
            </button>
          )}
          {canStop && (
            <>
              <button
                onClick={() => handleAction(stop)}
                disabled={loading}
                className="flex-1 min-w-[80px] py-2.5 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-1 sm:gap-2 text-sm sm:text-base"
              >
                {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faStop} />}
                <span>Stop</span>
              </button>
              <button
                onClick={() => handleAction(restart)}
                disabled={loading}
                className="flex-1 min-w-[80px] py-2.5 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-1 sm:gap-2 text-sm sm:text-base"
              >
                {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faRotate} />}
                <span>Restart</span>
              </button>
            </>
          )}
          {container.state === 'running' && (
            <button
              onClick={() => setShowTerminal(true)}
              className="flex-1 min-w-[80px] py-2.5 rounded-lg bg-purple-500 text-white hover:bg-purple-600 transition-colors flex items-center justify-center gap-1 sm:gap-2 text-sm sm:text-base"
            >
              <FontAwesomeIcon icon={faTerminal} />
              <span>Terminal</span>
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 px-3 sm:px-5 pb-4">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-2 sm:px-4 py-2.5 rounded-xl font-medium transition-all flex items-center justify-center gap-1 sm:gap-2 text-sm sm:text-base ${
                activeTab === tab.id
                  ? 'bg-theme-button-color text-theme-button-text-color shadow-md'
                  : 'text-theme-hint-color hover:bg-theme-bg-color'
              }`}
            >
              <FontAwesomeIcon icon={tab.icon} className="text-sm sm:text-base" />
              <span className="hidden xs:inline">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'info' && (
          <div className="h-full overflow-y-auto p-4 space-y-4">
            {loadingDetails ? (
              <div className="flex items-center justify-center h-64">
                <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-theme-button-color" />
              </div>
            ) : details ? (
              <>
                {/* Basic Info */}
                <div className="bg-theme-secondary-bg-color rounded-lg p-4">
                  <h3 className="text-theme-text-color font-semibold mb-3">Basic Info</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-theme-hint-color">ID:</span>
                      <span className="text-theme-text-color ml-2 font-mono text-xs">{details.Id?.substring(0, 12)}</span>
                    </div>
                    <div>
                      <span className="text-theme-hint-color">Image:</span>
                      <span className="text-theme-text-color ml-2">{details.Config?.Image}</span>
                    </div>
                    <div>
                      <span className="text-theme-hint-color">Created:</span>
                      <span className="text-theme-text-color ml-2">{new Date(details.Created).toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Network */}
                {details.NetworkSettings?.Networks && (
                  <div className="bg-theme-secondary-bg-color rounded-lg p-4">
                    <h3 className="text-theme-text-color font-semibold mb-3">Networks</h3>
                    {Object.entries(details.NetworkSettings.Networks).map(([name, network]) => (
                      <div key={name} className="text-sm mb-2">
                        <div className="text-theme-link-color font-medium">{name}</div>
                        <div className="text-theme-hint-color">IP: {network.IPAddress || 'N/A'}</div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Ports */}
                {details.NetworkSettings?.Ports && Object.keys(details.NetworkSettings.Ports).length > 0 && (
                  <div className="bg-theme-secondary-bg-color rounded-lg p-4">
                    <h3 className="text-theme-text-color font-semibold mb-3">Ports</h3>
                    <div className="space-y-1 text-sm">
                      {Object.entries(details.NetworkSettings.Ports).map(([containerPort, hostPorts]) => (
                        <div key={containerPort} className="text-theme-text-color">
                          {containerPort} → {hostPorts ? hostPorts.map(h => `${h.HostIp}:${h.HostPort}`).join(', ') : 'not mapped'}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Environment Variables */}
                {details.Config?.Env && details.Config.Env.length > 0 && (
                  <div className="bg-theme-secondary-bg-color rounded-lg p-4">
                    <h3 className="text-theme-text-color font-semibold mb-3">Environment Variables</h3>
                    <div className="space-y-1 text-xs font-mono">
                      {details.Config.Env.map((env, i) => (
                        <div key={i} className="text-theme-hint-color break-all">{env}</div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Mounts */}
                {details.Mounts && details.Mounts.length > 0 && (
                  <div className="bg-theme-secondary-bg-color rounded-lg p-4">
                    <h3 className="text-theme-text-color font-semibold mb-3">Volumes</h3>
                    {details.Mounts.map((mount, i) => (
                      <div key={i} className="text-sm mb-2">
                        <div className="text-theme-link-color">{mount.Type}</div>
                        <div className="text-theme-hint-color text-xs break-all">
                          {mount.Source} → {mount.Destination}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="text-theme-hint-color text-center py-8">Failed to load details</div>
            )}
          </div>
        )}

        {activeTab === 'stats' && <ContainerStats containerId={container.id} />}
        {activeTab === 'logs' && <ContainerLogs containerId={container.id} />}
      </div>
    </div>
  );
}
